import Card from '../card/Card';
import H from '../texts/h/H';
import P from '../texts/p/P';
import Button from '../button/Button';
import IconButton from '../icon-button/IconButton';
import TableSkeleton from '../TableSkeleton/TableSkeleton';
import GeneralSection from '../../organisms/MonetizationNew/GeneralSection';
import LoanSection from '../../organisms/MonetizationNew/LoanSection';
import CheckSection from '../../organisms/MonetizationNew/CheckSection';
import PromissoryNoteSection from '../../organisms/MonetizationNew/PromissoryNoteSection';
import CommercialEndorsementsSection from '../../organisms/MonetizationNew/CommercialEndorsementsSection';
import ObservationSection from '../../organisms/MonetizationNew/ObservationSection';
import LoanExecutionSection from '../../organisms/MonetizationNew/LoanExecutionSection';
import CheckPromissoryExecutionSection from '../../organisms/MonetizationNew/CheckPromissoryExecutionSection';
import { useMonetizationForm } from '../../hooks/useMonetizationForm';
import { Toast, useToast } from '../toast/toast';
import { downloadMonetizationPdf } from '../../services/MonetizationService';
import type { PrefilledData } from '../../pages/MonetizationNewPrefilled';
import { getMonetizationOperationType } from '../../utils/monetizationExecution';
import { useEffect, useMemo, useState } from 'react';
import { getQuotations, Quotation } from '../../services/QuotationsService';
import {
  currencySymbol,
  isLocalCurrencyCode,
  resolveCurrencyCode,
} from '../../utils/currencyLabel';
import { useConfig } from '../../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';

interface MonetizationFormProps {
  mode: 'create' | 'edit';
  id?: string;
  operationType?: string;
  saveBeforeExecute?: boolean;
  prefilledMode?: boolean;
  prefilledData?: PrefilledData;
}

const MonetizationForm = ({ 
  mode, 
  id, 
  operationType, 
  saveBeforeExecute = false,
  prefilledMode = false,
  prefilledData
}: MonetizationFormProps) => {
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const {
    formData,
    formValues,
    loading,
    submitting,
    error, // Only used for initial loading errors
    updateFormValues,
    handleGoBack,
    handleSubmit,
    handleSave,
    handleExecute,
    isFormValid,
    isExecuteValid,
    installments,
    clearing,
    isExecuted,
    isGrouped,
    groupedInfo,
    availableOperable,
    operableAmountError,
    amountInputStatus,
    loanDbId,
    handleInstallmentsRedefined,
    showManualInstallmentPlan,
    manualInstallmentPlanProps,
  } = useMonetizationForm({ mode, id, operationType, prefilledMode, prefilledData });

  const currencyCode = useMemo(
    () =>
      resolveCurrencyCode(
        formData?.currency ?? prefilledData?.currencyCode,
        localCurrencyCode
      ),
    [formData?.currency, localCurrencyCode, prefilledData?.currencyCode]
  );

  const [usdQuotation, setUsdQuotation] = useState<number | null>(null);

  useEffect(() => {
    if (isLocalCurrencyCode(currencyCode)) {
      setUsdQuotation(null);
      return;
    }
    getQuotations()
      .then((res) => {
        if (res?.data && Array.isArray(res.data)) {
          const usd = res.data.find((q: Quotation) => q.code === 'USD');
          if (usd) {
            const val =
              typeof usd.saleValue === 'string' ? parseFloat(usd.saleValue) : usd.saleValue;
            setUsdQuotation(val > 0 ? val : null);
          } else {
            setUsdQuotation(null);
          }
        }
      })
      .catch(() => setUsdQuotation(null));
  }, [currencyCode]);

  if (loading) {
    return (
      <>
        <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
          <Card margin={{ top: '4' }}>
            <TableSkeleton rows={8} columns={4} />
          </Card>
        </Card>
        <Toast id='monetization-toast' />
      </>
    );
  }

  if (error || !formData) {
    return (
      <>
        <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
          <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
            <H type="1" weight="regular" modifier="weak">{error || 'Datos no encontrados'}</H>
          </Card>
        </Card>
        <Toast id='monetization-toast' />
      </>
    );
  }

  const renderConditionalSection = () => {
    const operationType = formData.product_line_label;
    
    if (operationType === 'CDP Propios' || operationType === 'CDP Terceros') {
      return (
        <CheckSection 
          formData={formData}
          formValues={formValues}
          updateFormValues={updateFormValues}
          currencyCode={currencyCode}
          usdQuotation={usdQuotation}
        />
      );
    }
    
    if (operationType === 'Pagaré Dólar Link' || operationType === 'Pagaré en Pesos') {
      return (
        <PromissoryNoteSection 
          formData={formData}
          formValues={formValues}
          updateFormValues={updateFormValues}
          currencyCode={currencyCode}
          usdQuotation={usdQuotation}
        />
      );
    }
    
    if (operationType === 'Aval Comercial') {
      return (
        <CommercialEndorsementsSection 
          formData={formData}
          formValues={formValues}
          updateFormValues={updateFormValues}
          amountInputStatus={amountInputStatus}
          amountInputMessage={operableAmountError ?? undefined}
          currencyCode={currencyCode}
          usdQuotation={usdQuotation}
        />
      );
    }
    
    switch (operationType) {
      case 'Préstamo':
      case 'Obligación Negociable':
      case 'Obligaciones Negociables':
        return (
          <LoanSection 
            formData={formData}
            formValues={formValues}
            updateFormValues={updateFormValues}
            operationType={operationType}
            amountInputStatus={amountInputStatus}
            amountInputMessage={operableAmountError ?? undefined}
            currencyCode={currencyCode}
            usdQuotation={usdQuotation}
            showManualInstallmentPlan={showManualInstallmentPlan}
            manualInstallmentPlan={manualInstallmentPlanProps}
          />
        );
      case 'Operaciones Públicas':
        return (
          <LoanSection 
            formData={formData}
            formValues={formValues}
            updateFormValues={updateFormValues}
            operationType={operationType}
            amountInputStatus={amountInputStatus}
            amountInputMessage={operableAmountError ?? undefined}
            currencyCode={currencyCode}
            usdQuotation={usdQuotation}
            showManualInstallmentPlan={showManualInstallmentPlan}
            manualInstallmentPlan={manualInstallmentPlanProps}
          />
        );
      case 'Cheque':
        return (
          <CheckSection 
            formData={formData}
            formValues={formValues}
            updateFormValues={updateFormValues}
            amountInputStatus={amountInputStatus}
            amountInputMessage={operableAmountError ?? undefined}
            currencyCode={currencyCode}
            usdQuotation={usdQuotation}
          />
        );
      case 'Pagaré':
        return (
          <PromissoryNoteSection 
            formData={formData}
            formValues={formValues}
            updateFormValues={updateFormValues}
            amountInputStatus={amountInputStatus}
            amountInputMessage={operableAmountError ?? undefined}
            currencyCode={currencyCode}
            usdQuotation={usdQuotation}
          />
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    return mode === 'create' ? 'Nueva monetización' : `Pendiente de ejecución nº ${formValues.id_global?.toString()}`;
  };

  const renderExecutionSection = () => {
    const operationType = formData.product_line_label;
    
    // For loans and Obligación Negociable
    if (operationType === 'Préstamo' || operationType === 'Operaciones Públicas' || operationType === 'Obligación Negociable' || operationType === 'Obligaciones Negociables') {
      return (
        <LoanExecutionSection
          formValues={formValues}
          updateFormValues={updateFormValues}
          installments={installments}
          idGlobal={formValues.id_global?.toString()}
          isPublicOperation={operationType === 'Operaciones Públicas'}
          loanDbId={loanDbId}
          onInstallmentsRedefined={handleInstallmentsRedefined}
        />
      );
    }
    
    // For checks
    if (operationType === 'CDP Propios' || operationType === 'CDP Terceros' || operationType === 'Cheque') {
      return (
        <CheckPromissoryExecutionSection
          formValues={formValues}
          updateFormValues={updateFormValues}
          clearing={clearing || 0}
          isCheck={true}
        />
      );
    }
    
    // For promissory notes
    if (operationType === 'Pagaré Dólar Link' || operationType === 'Pagaré en Pesos' || operationType === 'Pagaré') {
      return (
        <CheckPromissoryExecutionSection
          formValues={formValues}
          updateFormValues={updateFormValues}
          clearing={clearing ?? undefined}
          isCheck={false}
        />
      );
    }
    
    // For commercial endorsements
    if (operationType === 'Aval Comercial') {
      return (
        <CheckPromissoryExecutionSection
          formValues={formValues}
          updateFormValues={updateFormValues}
          clearing={clearing ?? undefined}
          isCheck={false}
          isCommercialEndorsement={true}
        />
      );
    }
    
    return null;
  };

  const handleDownloadContract = async () => {
    const idGlobal = formValues.id_global?.toString();
    if (!idGlobal) return;
    
    try {
      const pdfResponse = await downloadMonetizationPdf(idGlobal);
      if (pdfResponse.success && 'data' in pdfResponse && pdfResponse.data) {
        const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `contrato_${idGlobal}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        useToast.error({
          title: 'Error',
          description: 'No se pudo descargar el contrato',
          id: 'monetization-toast',
          autoClose: 5000,
          closeButton: true,
        });
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
      useToast.error({
        title: 'Error',
        description: 'Error al descargar el contrato',
        id: 'monetization-toast',
        autoClose: 5000,
        closeButton: true,
      });
    }
  };

  const renderActionButtons = () => {
    const isLoan = formData?.product_line_label === 'Préstamo';

    if (mode === 'create') {
      const buttonText = isLoan 
        ? 'Confirmar creación y descargar certificado de garantía' 
        : 'Crear monetización';
      
      return (
        <Button 
          variant='primary'
          onClick={handleSubmit}
          disabled={submitting || !isFormValid()}
        >
          {submitting ? 'Creando...' : buttonText}
        </Button>
      );
    }

    // Edit mode buttons
    return (
      <>
        <Button 
          variant='contained'
          onClick={handleSave}
          disabled={submitting}
        >
          Guardar
        </Button>
        <Button 
          variant='primary'
          onClick={async () => {
            if (isExecuted) return;
            if (saveBeforeExecute) {
              const saved = await handleSave();
              if (!saved) return;
              // Pass true to skip file upload since handleSave already uploaded it
              await handleExecute(true);
            } else {
              await handleExecute();
            }
          }}
          disabled={isExecuted || submitting || !isExecuteValid()}
        >
          {isExecuted ? 'Ejecutado' : (submitting ? 'Ejecutando...' : 'Confirmar ejecución')}
        </Button>
      </>
    );
  };

  const renderDownloadContractButton = () => {
    const isLoan = formData?.product_line_label === 'Préstamo';
    
    if (mode !== 'edit' || !isLoan) {
      return null;
    }

    return (
      <Button 
        variant='contained'
        onClick={handleDownloadContract}
        disabled={!formValues.id_global || submitting}
      >
        Descargar contrato
      </Button>
    );
  };

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and title */}
      <Card display='flex' alignItems='center' gap='8px' margin={{ bottom: '4' }}>
        <IconButton
          name='chevron_left_outline'
          ariaLabel='Volver'
          variant='primary'
          modifier='regular'
          size={24}
          onClick={handleGoBack}
        />
        <H withMargin={false} variant="secondary-2" type='2' weight='bold' modifier='strong'>
          {getTitle()}
        </H>
      </Card>

      {/* Task 50115: in grouped mode show the FIFO disponible (single source of
         truth from ms-guarantees, fetched in the hook) so the user can never
         submit an amount the backend will reject. */}
      {mode === 'create' && isGrouped && groupedInfo?.totalAvailableAmount != null && (
        <Card
          variant='neutral'
          modifier='regular'
          spacing='3'
          margin={{ bottom: '3' }}
          display='flex'
          alignItems='center'
          justifyContent='space-between'
        >
          <P withMargin={false} type='3' weight='bold' modifier='strong'>
            Monetización agrupada — disponible operable
          </P>
          <Card display='flex' gap='24px' alignItems='center'>
            {groupedInfo.totalApprovedAmount != null && (
              <P withMargin={false} type='3' weight='regular' modifier='weak'>
                Total: {groupedInfo.totalApprovedAmount.toLocaleString('es-AR')}
              </P>
            )}
            {groupedInfo.totalConsumedAmount != null && (
              <P withMargin={false} type='3' weight='regular' modifier='weak'>
                Utilizado: {groupedInfo.totalConsumedAmount.toLocaleString('es-AR')}
              </P>
            )}
            <H withMargin={false} variant='secondary-2' type='3' weight='bold' modifier='strong'>
              Disponible operable: {(groupedInfo.availableOperable ?? availableOperable ?? groupedInfo.totalAvailableAmount).toLocaleString('es-AR')}
            </H>
          </Card>
        </Card>
      )}

      {/* Prefilled mode: Show effective capacity banner */}
      {mode === 'create' && prefilledMode && prefilledData && (
        <Card
          margin={{ bottom: '4' }}
          display='flex'
          alignItems='center'
          justifyContent='center'
          gap='8px'
          style={{ border: '1px solid #03BAA1', backgroundColor: '#E6F5F0', padding: '12px 24px', borderRadius: '4px' }}
        >
          <P withMargin={false} type='3' weight='regular' variant='neutral' modifier='strong'>
            Capacidad disponible para monetizar
          </P>
          <H withMargin={false} type='3' weight='bold' variant='neutral' modifier='strong'>
            {currencySymbol(prefilledData.currencyCode)} {(prefilledData.availableOperable ?? prefilledData.effectiveCapacity).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </H>
        </Card>
      )}

      {mode === 'create' && operableAmountError && (
        <Card variant='danger' modifier='weak' spacing='3' margin={{ bottom: '3' }}>
          <P withMargin={false} type='3' weight='regular' variant='danger' modifier='strong'>
            {operableAmountError}
          </P>
        </Card>
      )}

      {/* General Section */}
      <GeneralSection 
        formData={formData}
        formValues={formValues}
        updateFormValues={updateFormValues}
        prefilledMode={prefilledMode}
        showAvalSindicado={
          getMonetizationOperationType(formData.product_line_label) === 'préstamo'
        }
      />

      {/* Conditional Sections based on operation type */}
      {renderConditionalSection()}

      {!['préstamo', 'pagaré', 'cheque'].includes(
        getMonetizationOperationType(formData.product_line_label) as string
      ) && (
        <ObservationSection
          formData={formData}
          formValues={formValues}
          updateFormValues={updateFormValues}
        />
      )}

      {/* Execution Section - Only for edit mode */}
      {mode === 'edit' && renderExecutionSection()}

      {/* Action Buttons */}
      <Card margin={{ top: '4' }} display='flex' justifyContent='space-between' alignItems='center'>
        <Card>
          {renderDownloadContractButton()}
        </Card>
        <Card display='flex' alignItems='center' gap='32px'>
          {renderActionButtons()}
        </Card>
      </Card>
      
      <Toast id='monetization-toast' />
    </Card>
  );
};

export default MonetizationForm;
